import { useEffect, useRef, useState } from 'react';
import { useSimStore } from '../../store/useSimStore';
import { eegDerived, EEG_BRANDS } from '../../engine/eegEngine';
import { EegTrace, DsaCanvas, TrendCanvas } from './EegCanvases';
import styles from './EegMonitor.module.css';

/**
 * Depth-of-anaesthesia monitor, presented as one of three real devices:
 * BIS™ (Medtronic), GE Entropy, or Masimo SedLine®. All read the store's
 * `bisValue`; the brand changes the index name, derived numbers, colours
 * and panels (SedLine adds four EEG leads + the DSA spectrogram).
 */
export default function EegMonitor() {
  const bis = useSimStore((s) => s.bisValue);
  const brand = useSimStore((s) => s.eegMonitor.brand);
  const patientName = useSimStore((s) => s.patientName);
  const meta = EEG_BRANDS[brand] ?? EEG_BRANDS.bis;
  const d = eegDerived(bis);

  // Canvases read the live value through refs (no re-render per frame).
  const indexRef = useRef(bis);
  indexRef.current = bis;
  const sefRef = useRef(d.sef);
  sefRef.current = d.sef;
  const srRef = useRef(d.sr);
  srRef.current = d.sr;

  const [clock, setClock] = useState('');
  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString([], { hour12: false }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const bigValue = brand === 'entropy' ? d.se : brand === 'sedline' ? d.psi : d.bis;
  const band = brand === 'sedline' ? [25, 50] : [40, 60];
  const inBand = bigValue >= band[0] && bigValue <= band[1];
  const stateLabel = bigValue > 80 ? 'AWAKE' : bigValue > 60 ? 'LIGHT SEDATION' : bigValue >= band[0] ? 'GENERAL ANAESTHESIA' : bigValue > 20 ? 'DEEP HYPNOSIS' : 'BURST SUPPRESSION';

  return (
    <div className={`${styles.screen} ${styles[brand] ?? ''}`} style={{ '--accent': meta.accent }}>
      <div className={styles.header}>
        <span className={styles.brand}>{meta.label}</span>
        <span className={styles.vendor}>{meta.vendor}</span>
        <span className={styles.headMeta}>{patientName}</span>
        <span className={styles.clock}>{clock}</span>
      </div>

      <div className={styles.body}>
        {/* ── Index + secondary numerics ─────────────────────────────────── */}
        <div className={styles.numerics}>
          <div className={styles.indexBlock}>
            <span className={styles.indexLabel}>{meta.index}</span>
            <span className={`${styles.indexValue} ${inBand ? styles.inBand : ''}`}>{bigValue}</span>
            <span className={styles.indexRange}>{meta.range}</span>
            <span className={styles.stateLabel}>{stateLabel}</span>
          </div>

          {brand === 'bis' && (
            <div className={styles.subGrid}>
              <Sub label="SQI" value={d.sqi} unit="%" bar={d.sqi} />
              <Sub label="EMG" value={d.emg} unit="dB" bar={(d.emg - 20) / 40 * 100} />
              <Sub label="SR" value={d.sr} unit="%" bar={d.sr} warn={d.sr > 0} />
              <Sub label="SEF" value={d.sef} unit="Hz" />
            </div>
          )}
          {brand === 'entropy' && (
            <div className={styles.subGrid}>
              <Sub label="RE" value={d.re} big />
              <Sub label="SE" value={d.se} big />
              <Sub label="BSR" value={d.bsr} unit="%" warn={d.bsr > 0} />
              <Sub label="RE − SE" value={d.re - d.se} />
            </div>
          )}
          {brand === 'sedline' && (
            <div className={styles.subGrid}>
              <Sub label="EMG" value={d.emg} unit="dB" bar={(d.emg - 20) / 40 * 100} />
              <Sub label="SR" value={d.sr} unit="%" bar={d.sr} warn={d.sr > 0} />
              <Sub label="SEFL" value={d.sef} unit="Hz" />
              <Sub label="SEFR" value={(d.sef - 0.3).toFixed(1)} unit="Hz" />
              <Sub label="ARTF" value={d.artf} unit="%" />
            </div>
          )}
        </div>

        {/* ── Waveforms ─────────────────────────────────────────────────── */}
        <div className={styles.waves}>
          {brand === 'sedline' ? (
            <>
              <div className={styles.leadGrid}>
                {['L1', 'R1', 'L2', 'R2'].map((lead, i) => (
                  <div key={lead} className={styles.lead}>
                    <span className={styles.leadTag}>{lead}</span>
                    <EegTrace indexRef={indexRef} channel={i} color={meta.trace} windowSec={6} grid={false} />
                  </div>
                ))}
              </div>
              <div className={styles.dsaBlock}>
                <span className={styles.panelTag}>DSA · 0–30 Hz · 4 min</span>
                <DsaCanvas indexRef={indexRef} sefRef={sefRef} accent={meta.accent} />
              </div>
            </>
          ) : (
            <div className={styles.eegBlock}>
              <span className={styles.panelTag}>EEG · 8 s · {brand === 'entropy' ? 'FEMG' : 'Fp1–Fpz'}</span>
              <EegTrace indexRef={indexRef} color={meta.trace} windowSec={8} />
            </div>
          )}
          <div className={styles.trendBlock}>
            <span className={styles.panelTag}>{meta.index} trend · 30 min{brand !== 'entropy' ? ' · SR in red' : ''}</span>
            <TrendCanvas indexRef={indexRef} band={band} color={meta.accent} srRef={brand === 'entropy' ? null : srRef} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Sub({ label, value, unit, bar, warn, big }) {
  return (
    <div className={`${styles.sub} ${warn ? styles.subWarn : ''} ${big ? styles.subBig : ''}`}>
      <span className={styles.subLabel}>{label}</span>
      <span className={styles.subValue}>{value}<span className={styles.subUnit}>{unit}</span></span>
      {bar != null && <span className={styles.subBar}><span style={{ width: `${Math.max(0, Math.min(100, bar))}%` }} /></span>}
    </div>
  );
}
